import { CSSProperties, ReactNode, useState, useEffect } from "react";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { MobileNav } from "./MobileNav";
import { MobileSidebar } from "./MobileSidebar";
import { GlobalSearch } from "@/components/GlobalSearch";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useTimezoneCapture } from "@/hooks/useTimezoneCapture";
import { useCurrentUserTimezone } from "@/hooks/useCurrentUserTimezone";
import { PushPermissionPrompt } from "@/components/reminders/PushPermissionPrompt";
import { RemindersBanner } from "@/components/reminders/RemindersBanner";
import { cn } from "@/lib/utils";

interface AppLayoutProps {
  children: ReactNode;
  title: string;
  /**
   * Desktop only: the page is exactly one screen tall and scrolls inside its
   * own panels. The dashboards use it so nothing ever needs a page scroll.
   */
  fill?: boolean;
}

export function AppLayout({ children, title, fill = false }: AppLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem("ftc-sidebar-collapsed") === "true"; } catch { return false; } });
  const toggleSidebar = () => setCollapsed(previous => {
    try { localStorage.setItem("ftc-sidebar-collapsed", String(!previous)); } catch { /* Keep the current-session preference when storage is unavailable. */ }
    return !previous;
  });
  const [searchOpen, setSearchOpen] = useState(false);
  useTimezoneCapture();
  const userTz = useCurrentUserTimezone();

  // Cmd+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="min-h-screen bg-background" style={{ "--sidebar-width": collapsed ? "72px" : "260px" } as CSSProperties}>
      <Sidebar collapsed={collapsed} onToggle={toggleSidebar} />
      
      <MobileSidebar open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} />
      
      <div
        className={cn(
          "md:ml-[var(--sidebar-width)] transition-all duration-300 pb-20 md:pb-0",
          fill && "lg:h-dvh lg:flex lg:flex-col lg:pb-0",
        )}
        data-testid="main-content"
      >
        {/* In flow, above the header, inside the sidebar offset. Previously this
            was rendered at the end of the layout as `fixed top-0 left-0
            right-0`, which painted it over the header and across the sidebar —
            the page title and search box were underneath it. */}
        <RemindersBanner userTimezone={userTz} />
        <Header
          title={title}
          onMenuClick={() => setMobileMenuOpen(true)}
          onSearchClick={() => setSearchOpen(true)}
        />
        <main
          className={cn(
            "p-4 md:p-6 animate-fade-in",
            fill && "lg:flex-1 lg:min-h-0 lg:flex lg:flex-col lg:px-5 lg:py-3",
          )}
        >
          <ErrorBoundary area={title}>{children}</ErrorBoundary>
        </main>
      </div>
      
      <MobileNav />
      
      {/* Global Search Modal */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Push notification permission prompt */}
      <PushPermissionPrompt />
    </div>
  );
}
